import React from "react";
import { Tag, MessageCircle, ArrowDownLeft, ArrowUpRight, X, RefreshCw } from "lucide-react";
import Popup from "reactjs-popup";
import { formatCallDuration, callStatusLabel } from "./utils";

const RecentCalls = ({
  recentCalls,
  isRefreshing,
  onRefresh,
  openCommentPopup,
  commentCallIndex,
  commentCall,
  commentDraft,
  setCommentDraft,
  closeCommentPopup,
  saveComment,
  savingComment,
  reportTags,
  tagCallIndex,
  tagCall,
  openTagPopup,
  closeTagPopup,
  saveTag,
  removeTag,
  savingTag,
}) => {
  return (
    <div className="card">
      <Popup
        open={commentCallIndex !== null}
        modal
        nested
        position="center center"
        contentStyle={{ width: "380px", padding: 0, borderRadius: "14px", border: "none" }}
        overlayStyle={{ background: "rgba(0,0,0,0.5)" }}
        onClose={closeCommentPopup}
      >
        {() => (
          <div className="dash2 call-comment-popup">
            <div className="call-comment-head">
              <div>
                <h3>{commentCall?.customer_number}</h3>
                <div className="call-comment-sub">{commentCall?.call_time}</div>
              </div>
              <button type="button" className="campaign-picker-close" onClick={closeCommentPopup}>
                ×
              </button>
            </div>
            <div className="call-comment-body">
              <label>{commentCall?.comment ? "Edit Comment" : "Add Comment"}</label>
              <textarea
                rows={4}
                placeholder="Type a note about this call..."
                value={commentDraft}
                onChange={(e) => setCommentDraft(e.target.value)}
              />
              <button
                type="button"
                className="call-comment-save"
                disabled={!commentDraft.trim() || savingComment}
                onClick={saveComment}
              >
                {savingComment ? "Saving…" : "Save Comment"}
              </button>
            </div>
          </div>
        )}
      </Popup>

      <Popup
        open={tagCallIndex !== null}
        modal
        nested
        position="center center"
        contentStyle={{ width: "380px", padding: 0, borderRadius: "14px", border: "none" }}
        overlayStyle={{ background: "rgba(0,0,0,0.5)" }}
        onClose={closeTagPopup}
      >
        {() => (
          <div className="dash2 call-tag-popup">
            <div className="call-comment-head">
              <div>
                <h3>{tagCall?.customer_number}</h3>
                <div className="call-comment-sub">{tagCall?.call_time}</div>
              </div>
              <button type="button" className="campaign-picker-close" onClick={closeTagPopup}>
                ×
              </button>
            </div>
            <div className="call-tag-body">
              <label>Select a Tag</label>
              {reportTags.length === 0 ? (
                <div className="empty-state">No tags available.</div>
              ) : (
                <div className="call-tag-list">
                  {reportTags.map((tag) => {
                    const applied = tagCall?.report_tags?.includes(tag);
                    return (
                      <button
                        type="button"
                        key={tag}
                        className={`call-tag-chip${applied ? " active" : ""}`}
                        disabled={savingTag}
                        onClick={() => (applied ? removeTag(tagCallIndex, tag) : saveTag(tag))}
                        title={applied ? `Click to remove "${tag}"` : tag}
                      >
                        {tag}
                        {applied && <X size={11} className="call-tag-chip-remove" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </Popup>

      <div className="card-head">
        <h2>Recent Calls</h2>
        <div
          className="icon-btn"
          title="Refresh dashboard"
          onClick={onRefresh}
        >
          <RefreshCw size={15} className={isRefreshing ? "spin" : ""} />
        </div>
      </div>
      <div className="call-list">
        {recentCalls.length === 0 ? (
          <div className="empty-state">No recent calls to show.</div>
        ) : (
          recentCalls.map((call, index) => {
            const answered = call.call_status === "ANSWERED";
            const incoming = call.call_direction === "incoming";
            return (
              <div className="call-row" key={index}>
                <div className="call-left">
                  <div
                    className="call-icon"
                    style={{ background: "var(--primary)" }}
                  >
                    <svg viewBox="0 0 24 24" style={{ fill: "#fff" }}>
                      <circle cx="12" cy="8" r="4" />
                      <path d="M4 20c0-4.4 3.6-7 8-7s8 2.6 8 7v1H4z" />
                    </svg>
                  </div>
                  <div className="call-meta">
                    <div className="name">{call.customer_number}</div>
                    <div className="sub">{call.agent_name}</div>
                    {call.report_tags?.length > 0 && (
                      <div className="call-tags">
                        {call.report_tags.map((tag) => (
                          <span key={tag} className="call-tag-badge">
                            <span className="call-tag-badge-text" title={tag}>
                              {tag}
                            </span>
                            <button
                              type="button"
                              className="call-tag-badge-remove"
                              disabled={savingTag}
                              onClick={() => removeTag(index, tag)}
                              title={`Remove "${tag}"`}
                            >
                              <X size={10} />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className={`call-direction ${incoming ? "incoming" : "outgoing"}`}>
                  {incoming ? <ArrowDownLeft size={14} /> : <ArrowUpRight size={14} />}
                  {incoming ? "Inbound" : "Outbound"}
                </div>
                <div className="call-status-col">
                  <div className={`status-chip ${answered ? "answered" : "not-answered"}`}>
                    {callStatusLabel(call.call_status)}
                  </div>
                </div>
                <div className="call-actions">
                  {call.source !== "predictive_dialer" && (
                    <button
                      type="button"
                      className="action-btn tag"
                      onClick={() => openTagPopup(index)}
                    >
                      <Tag size={13} />
                      <span>Tag</span>
                    </button>
                  )}
                  <button
                    type="button"
                    className="action-btn comment"
                    onClick={() => openCommentPopup(index)}
                  >
                    <MessageCircle size={13} />
                    {call.comment ? "Edit Comment" : "Comment"}
                  </button>
                </div>
                <div className="call-right">
                  <div className="call-time">{call.call_time}</div>
                  <div className="call-dur">{formatCallDuration(call.call_duration)}</div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default RecentCalls;
